/**
 * Fragment de routes du paquet v6-p04-exploitation-ecrans (vague V6, règle R2 du découpage V4–V11 : un fragment par paquet).
 * Composants chargés à la demande (règle de chargement du registre), aucun code exécuté au chargement.
 * Routes du socle remplacées (`remplace: true`), d'après les `couvre: route:` des YAML : `/incidents` (projet-incidents),
 * `/incidents/:id` (projet-incident-fiche), `/exploitation/supervision` (projet-supervision-application),
 * `/exploitation/objectifs/:id` (projet-engagement-fiche), `/documentation` (projet-documentation). Les écrans historiques
 * (`screens/exploitation/*`, `screens/ProjectDocScreen`) ne sont plus servis à ces chemins ; leurs fichiers et App.tsx
 * (gelé) restent inchangés.
 * Routes neuves (projet par `?projet=`) : `/incidents/:id/bilan`, `/exploitation/incidents/declarer`,
 * `/exploitation/engagements`, `/exploitation/engagements/declarer`, `/exploitation/service?environnement=`,
 * `/exploitation/mise-en-service?deploiement=&version=&environnement=`, `/documentation/page?page=`.
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import type { ContexteRoutes, FragmentRoutes } from './registre'

const ProjetIncidents = lazy(() => import('../ecrans/v6/exploitation/ProjetIncidents'))
const ProjetIncidentFiche = lazy(() => import('../ecrans/v6/exploitation/ProjetIncidentFiche'))
const ProjetBilanIncident = lazy(() => import('../ecrans/v6/exploitation/ProjetBilanIncident'))
const DialogueDeclarerIncident = lazy(() => import('../ecrans/v6/exploitation/DialogueDeclarerIncident'))
const ProjetSupervisionApplication = lazy(() => import('../ecrans/v6/exploitation/ProjetSupervisionApplication'))
const ProjetEngagementsDeService = lazy(() => import('../ecrans/v6/exploitation/ProjetEngagementsDeService'))
const DialogueDeclarerEngagement = lazy(() => import('../ecrans/v6/exploitation/DialogueDeclarerEngagement'))
const ProjetEngagementFiche = lazy(() => import('../ecrans/v6/exploitation/ProjetEngagementFiche'))
const ProjetServiceEnvironnement = lazy(() => import('../ecrans/v6/exploitation/ProjetServiceEnvironnement'))
const ProjetMiseEnServiceFiche = lazy(() => import('../ecrans/v6/exploitation/ProjetMiseEnServiceFiche'))
const ProjetDocumentation = lazy(() => import('../ecrans/v6/exploitation/ProjetDocumentation'))
const ProjetPageDocumentationFiche = lazy(() => import('../ecrans/v6/exploitation/ProjetPageDocumentationFiche'))

function ecran(Composant: ComponentType<{ session: Session }>) {
  return ({ session, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={<p role="status">{t('v10.primitives.assemblage.chargement')}</p>}>
      <Composant session={session} />
    </Suspense>
  )
}

export const fragment: FragmentRoutes = {
  zone: 'v6-exploitation',
  routes: [
    { chemin: '/incidents', element: ecran(ProjetIncidents), remplace: true },
    { chemin: '/incidents/:id', element: ecran(ProjetIncidentFiche), remplace: true },
    { chemin: '/incidents/:id/bilan', element: ecran(ProjetBilanIncident) },
    { chemin: '/exploitation/incidents/declarer', element: ecran(DialogueDeclarerIncident) },
    { chemin: '/exploitation/supervision', element: ecran(ProjetSupervisionApplication), remplace: true },
    { chemin: '/exploitation/engagements', element: ecran(ProjetEngagementsDeService) },
    { chemin: '/exploitation/engagements/declarer', element: ecran(DialogueDeclarerEngagement) },
    { chemin: '/exploitation/objectifs/:id', element: ecran(ProjetEngagementFiche), remplace: true },
    { chemin: '/exploitation/service', element: ecran(ProjetServiceEnvironnement) },
    { chemin: '/exploitation/mise-en-service', element: ecran(ProjetMiseEnServiceFiche) },
    { chemin: '/documentation', element: ecran(ProjetDocumentation), remplace: true },
    { chemin: '/documentation/page', element: ecran(ProjetPageDocumentationFiche) },
  ],
}
